import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../layout/MainLayout';
import Card, { CardHeader, CardTitle, CardBody } from '../core/Card/Card';
import { Calendar, Clock, Video, MapPin, FileText, Users, ArrowLeft, Save } from 'lucide-react';
import { LoadingSpinner } from '../common/LoadingSpinner';
import api from '../../services/api';
import '../../styles/mentor-session-form.scss';

interface SessionFormData {
  relationship_id: string;
  scheduled_at: string;
  duration_minutes: number;
  topic: string;
  description: string;
  location: string;
  meeting_link: string;
  session_type: 'presential' | 'virtual' | 'hybrid';
}

const EditSessionPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<SessionFormData>({
    relationship_id: '',
    scheduled_at: '',
    duration_minutes: 60,
    topic: '',
    description: '',
    location: '',
    meeting_link: '',
    session_type: 'virtual'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (id) {
      loadSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadSession = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/mentor/sessions/${id}`);
      const session = response.data.data || response.data;

      // Convert scheduled_date (or scheduled_at) to datetime-local format
      const dateField = session.scheduled_date || session.scheduled_at;
      if (!dateField) {
        throw new Error('No scheduled date found');
      }

      const scheduledDate = new Date(dateField);
      if (isNaN(scheduledDate.getTime())) {
        throw new Error('Invalid date format');
      }

      const localDateTime = new Date(scheduledDate.getTime() - scheduledDate.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);

      // Determine session type based on available data
      let sessionType: 'presential' | 'virtual' | 'hybrid' = 'virtual';
      if (session.meeting_link && session.location) {
        sessionType = 'hybrid';
      } else if (session.location) {
        sessionType = 'presential';
      }

      setFormData({
        relationship_id: session.relationship_id || '',
        scheduled_at: localDateTime,
        duration_minutes: session.duration_minutes || 60,
        topic: session.title || session.topic || '', // API usa 'title' no 'topic'
        description: session.description || '',
        location: session.location || '',
        meeting_link: session.meeting_link || '',
        session_type: sessionType
      });
    } catch (error) {
      console.error('Error loading session:', error);
      setErrors({ load: 'Error al cargar la sesión' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof SessionFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.scheduled_at) {
      newErrors.scheduled_at = 'La fecha y hora son requeridas';
    }
    if (!formData.topic.trim()) {
      newErrors.topic = 'El tema es requerido';
    }
    if (formData.duration_minutes < 15 || formData.duration_minutes > 240) {
      newErrors.duration_minutes = 'La duración debe estar entre 15 y 240 minutos';
    }
    if (formData.session_type === 'virtual' && !formData.meeting_link.trim()) {
      newErrors.meeting_link = 'El link de reunión es requerido para sesiones virtuales';
    }
    if (formData.session_type === 'presential' && !formData.location.trim()) {
      newErrors.location = 'La ubicación es requerida para sesiones presenciales';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);

      const submitData = {
        ...formData,
        duration_minutes: Number(formData.duration_minutes)
      };

      await api.put(`/mentor/sessions/${id}`, submitData);

      navigate('/mentor/sessions');
    } catch (error: any) {
      console.error('Error updating session:', error);
      const errorMessage = error.response?.data?.message || 'Error al actualizar la sesión';
      setErrors({ submit: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/mentor/sessions');
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="session-form-page session-form-page--loading">
          <LoadingSpinner size="lg" text="Cargando sesión..." />
        </div>
      </MainLayout>
    );
  }

  if (errors.load) {
    return (
      <MainLayout>
        <div className="session-form-page">
          <div className="error-page">
            <h2>Error</h2>
            <p>{errors.load}</p>
            <button className="btn btn--primary" onClick={() => navigate('/mentor/sessions')}>
              Volver a sesiones
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="session-form-page">
        {/* Header */}
        <div className="page-header">
          <button className="btn-back" onClick={handleCancel}>
            <ArrowLeft size={20} />
          </button>
          <div className="page-header__info">
            <div className="eyebrow">Mentor · Editar Sesión</div>
            <h1 className="heading-1">Editar Sesión</h1>
            <p className="page-description">
              Actualiza la información de esta sesión de mentoría
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="session-form">
          <div className="form-grid">
            {/* Left Column - Main Info */}
            <div className="form-column">
              <Card>
                <CardHeader>
                  <CardTitle>Información de la sesión</CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="form-group">
                    <label>
                      <FileText size={16} />
                      Tema de la sesión *
                    </label>
                    <input
                      type="text"
                      value={formData.topic}
                      onChange={(e) => handleChange('topic', e.target.value)}
                      placeholder="Ej: Revisión de progreso trimestral"
                      className={errors.topic ? 'error' : ''}
                    />
                    {errors.topic && (
                      <span className="error-message">{errors.topic}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>
                      <FileText size={16} />
                      Descripción
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder="Describe los objetivos y temas a tratar en esta sesión..."
                      rows={4}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        <Calendar size={16} />
                        Fecha y hora *
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.scheduled_at}
                        onChange={(e) => handleChange('scheduled_at', e.target.value)}
                        className={errors.scheduled_at ? 'error' : ''}
                      />
                      {errors.scheduled_at && (
                        <span className="error-message">{errors.scheduled_at}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label>
                        <Clock size={16} />
                        Duración (minutos) *
                      </label>
                      <input
                        type="number"
                        value={formData.duration_minutes}
                        onChange={(e) => handleChange('duration_minutes', e.target.value)}
                        min="15"
                        max="240"
                        step="15"
                        className={errors.duration_minutes ? 'error' : ''}
                      />
                      {errors.duration_minutes && (
                        <span className="error-message">{errors.duration_minutes}</span>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* Right Column - Location & Type */}
            <div className="form-column">
              <Card>
                <CardHeader>
                  <CardTitle>Tipo y ubicación</CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="form-group">
                    <label>Tipo de sesión *</label>
                    <div className="session-type-options">
                      <button
                        type="button"
                        className={`type-option ${formData.session_type === 'virtual' ? 'active' : ''}`}
                        onClick={() => handleChange('session_type', 'virtual')}
                      >
                        <Video size={20} />
                        <span>Virtual</span>
                      </button>
                      <button
                        type="button"
                        className={`type-option ${formData.session_type === 'presential' ? 'active' : ''}`}
                        onClick={() => handleChange('session_type', 'presential')}
                      >
                        <MapPin size={20} />
                        <span>Presencial</span>
                      </button>
                      <button
                        type="button"
                        className={`type-option ${formData.session_type === 'hybrid' ? 'active' : ''}`}
                        onClick={() => handleChange('session_type', 'hybrid')}
                      >
                        <Users size={20} />
                        <span>Híbrida</span>
                      </button>
                    </div>
                  </div>

                  {(formData.session_type === 'virtual' || formData.session_type === 'hybrid') && (
                    <div className="form-group">
                      <label>
                        <Video size={16} />
                        Link de reunión {formData.session_type === 'virtual' && '*'}
                      </label>
                      <input
                        type="url"
                        value={formData.meeting_link}
                        onChange={(e) => handleChange('meeting_link', e.target.value)}
                        placeholder="https://zoom.us/j/123456789"
                        className={errors.meeting_link ? 'error' : ''}
                      />
                      {errors.meeting_link && (
                        <span className="error-message">{errors.meeting_link}</span>
                      )}
                      <small>Google Meet, Zoom, Teams, etc.</small>
                    </div>
                  )}

                  {(formData.session_type === 'presential' || formData.session_type === 'hybrid') && (
                    <div className="form-group">
                      <label>
                        <MapPin size={16} />
                        Ubicación {formData.session_type === 'presential' && '*'}
                      </label>
                      <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => handleChange('location', e.target.value)}
                        placeholder="Oficina, sala de juntas, cafetería..."
                        className={errors.location ? 'error' : ''}
                      />
                      {errors.location && (
                        <span className="error-message">{errors.location}</span>
                      )}
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            {errors.submit && (
              <div className="error-banner">
                {errors.submit}
              </div>
            )}
            <button
              type="button"
              className="btn btn--outline"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={saving}
            >
              {saving ? (
                <>Guardando cambios...</>
              ) : (
                <>
                  <Save size={18} />
                  Guardar cambios
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default EditSessionPage;
